import React from 'react';
import { Button } from '../../../components/ui/button';
import { Clock, CheckCircle, XCircle, ShieldCheck, Lock } from 'lucide-react';

const STATUS_CONFIG = {
  pending_approval: {
    icon: Lock,
    color: 'amber',
    title: 'Pending Admin Approval - Entry Locked',
    text: 'Your variance approval request is awaiting admin review. The entry cannot be edited until the admin responds.',
  },
  approved: {
    icon: CheckCircle,
    color: 'green',
    title: 'Variance Approved',
    text: 'Admin has approved the variance. Your data is locked - click "Submit Approved Entry" below to submit it without changes.',
  },
  rejected: {
    icon: XCircle,
    color: 'red',
    title: 'Approval Request Rejected',
    text: 'Admin rejected the request. The entry is unlocked - please correct the data and resubmit.',
  },
  resolved_by_admin: {
    icon: ShieldCheck,
    color: 'blue',
    title: 'Resolved by Admin',
    text: 'Admin edited and submitted this entry on your behalf.',
  },
  completed: {
    icon: CheckCircle,
    color: 'green',
    title: 'Approved Entry Submitted',
    text: 'This entry was submitted after admin approval.',
  },
};

const COLOR_CLASSES = {
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-600',
  green: 'bg-green-500/10 border-green-500/30 text-green-600',
  red: 'bg-red-500/10 border-red-500/30 text-red-600',
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-600',
};

export const ApprovalStatusBanner = ({ approvalRequest, adminEditMode, onCancelAdminEdit, onSubmitApproved }) => {
  if (adminEditMode) {
    return (
      <div className={`flex items-center justify-between gap-4 p-4 border rounded-lg ${COLOR_CLASSES.blue}`} data-testid="admin-edit-mode-banner">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5" />
          <div>
            <p className="font-medium">Admin Edit &amp; Submit Mode</p>
            <p className="text-sm text-muted-foreground">
              Load the staff draft, adjust if needed, and submit. The variance check is bypassed. Cancelling keeps the request pending.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onCancelAdminEdit} data-testid="cancel-admin-edit-btn">Cancel</Button>
      </div>
    );
  }

  const config = approvalRequest && STATUS_CONFIG[approvalRequest.status];
  if (!config) return null;
  const Icon = config.icon || Clock;

  return (
    <div className={`flex items-start gap-3 p-4 border rounded-lg ${COLOR_CLASSES[config.color]}`} data-testid={`approval-status-banner-${approvalRequest.status}`}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div className="space-y-1">
        <p className="font-medium">{config.title}</p>
        <p className="text-sm text-muted-foreground">{config.text}</p>
        {approvalRequest.variance_details?.checks?.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            {approvalRequest.variance_details.checks.map(c => `${c.label}: £${(c.variance || 0).toFixed(2)}`).join(' | ')}
          </p>
        ) : approvalRequest.variance_amount != null && (
          <p className="text-xs text-muted-foreground">Variance: £{Math.abs(approvalRequest.variance_amount).toFixed(2)}</p>
        )}
        {approvalRequest.staff_comment && (
          <p className="text-sm" data-testid="approval-staff-comment">
            <span className="font-medium">Staff comment: </span>{approvalRequest.staff_comment}
          </p>
        )}
        {approvalRequest.resolution_notes && (
          <p className="text-sm" data-testid="approval-resolution-notes">
            <span className="font-medium">Admin notes: </span>{approvalRequest.resolution_notes}
          </p>
        )}
        {approvalRequest.status === 'approved' && onSubmitApproved && (
          <Button onClick={onSubmitApproved} className="mt-2 bg-green-600 hover:bg-green-700 text-white" data-testid="submit-approved-entry-btn">
            <CheckCircle className="h-4 w-4 mr-2" />Submit Approved Entry
          </Button>
        )}
      </div>
    </div>
  );
};
